/**
 * Where a guest's identity lives in the browser.
 *
 * Signing in is optional: a player can hit "Play as Guest" and the server mints
 * them a play key (see `utils/guestIdentity.ts` on the server). That key is the
 * whole account — it is what this module keeps in localStorage, and what a play
 * link carries so the same player can come back on another device.
 *
 * Everything here is defensive on purpose. localStorage throws in Safari's
 * private mode, can hold junk left by an older build, and can be cleared out
 * from under us; none of that should be able to break the lobby.
 */

export interface GuestSession {
  uid: string
  token: string
  name?: string
}

export const GUEST_SESSION_KEY = 'pg-guest-session'

/** The client route that redeems a play key. Kept here so links and routes agree. */
export const GUEST_LINK_PATH = '/guest'

const defaultStorage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

const nonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0

export const isGuestSession = (value: unknown): value is GuestSession => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as Record<string, unknown>

  return nonEmptyString(candidate.uid) && nonEmptyString(candidate.token)
}

export const readGuestSession = (storage: Storage | null = defaultStorage()): GuestSession | null => {
  try {
    const raw = storage?.getItem(GUEST_SESSION_KEY)

    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw)

    if (!isGuestSession(parsed)) {
      return null
    }

    return {
      uid: parsed.uid,
      token: parsed.token,
      ...(nonEmptyString(parsed.name) ? { name: parsed.name } : {}),
    }
  } catch {
    return null
  }
}

/**
 * Returns false when the browser refused to store it — the session still works
 * for this tab, but the player will only get back to their games via the link.
 */
export const saveGuestSession = (session: GuestSession, storage: Storage | null = defaultStorage()): boolean => {
  if (!isGuestSession(session)) {
    return false
  }

  try {
    storage?.setItem(GUEST_SESSION_KEY, JSON.stringify(session))

    return !!storage
  } catch {
    return false
  }
}

export const clearGuestSession = (storage: Storage | null = defaultStorage()): void => {
  try {
    storage?.removeItem(GUEST_SESSION_KEY)
  } catch {
    // Nothing to do — an unreadable store is an empty store as far as we care.
  }
}

/**
 * Mirror of the server's `GUEST_TOKEN_PATTERN` (`utils/guestIdentity.ts`): the
 * only keys the server would ever have minted. The server responds to an
 * implausible key by minting a brand NEW guest — deliberate leniency for its
 * other callers — but a play LINK carrying one is a mangled link, and
 * redeeming it would silently replace the stored session with a stranger.
 * The link screen checks this first and shows "damaged link" instead.
 */
const PLAY_KEY_PATTERN = /^[A-Za-z0-9_-]{32,128}$/

export const isPlausiblePlayKey = (token: unknown): token is string =>
  typeof token === 'string' && PLAY_KEY_PATTERN.test(token)

const currentOrigin = () => (typeof window === 'undefined' ? '' : window.location.origin)

const decodeSegment = (raw: string): string | null => {
  try {
    return decodeURIComponent(raw) || null
  } catch {
    return raw || null
  }
}

/**
 * The link a guest saves to get back to their games.
 *
 * The key goes in the FRAGMENT (`/guest#<key>`), not the path: browsers never
 * send a fragment to the server, so the key stays out of nginx's access log,
 * out of the Referer header and out of any proxy in between. The path form is
 * still understood when redeeming (see `guestTokenFromLocation`) so a link
 * mangled by a chat client that strips fragments isn't automatically dead.
 */
export const guestPlayUrl = (token: string, origin: string = currentOrigin()): string =>
  `${origin.replace(/\/+$/, '')}${GUEST_LINK_PATH}#${encodeURIComponent(token)}`

/** The key inside a play link, or null when the path isn't one. */
export const guestTokenFromPath = (path: unknown): string | null => {
  if (typeof path !== 'string') {
    return null
  }

  const match = path.match(/^\/guest\/([^/?#]+)/)

  return match ? decodeSegment(match[1]) : null
}

export const guestTokenFromHash = (hash: unknown): string | null => {
  if (typeof hash !== 'string') {
    return null
  }

  const raw = hash.replace(/^#/, '').split(/[?&]/)[0]

  return raw ? decodeSegment(raw) : null
}

/** Both shapes of play link, fragment first. */
export const guestTokenFromLocation = (location?: { pathname?: unknown, hash?: unknown } | null): string | null =>
  guestTokenFromHash(location?.hash) ?? guestTokenFromPath(location?.pathname)
